import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, LogOut } from 'lucide-react';
import { getLearnerNavItems } from './navigationHelpers';
import { dataLayer } from '../../data/dataLayer';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useBodyScrollLock } from '../../app/hooks/useBodyScrollLock';
import { APP_NAME, getProgressToNextLevel } from '../../config/constants';
import { useDjaySelection, DjayAvatar, DjayEmotion, useVariantUnlocks, TOTAL_VARIANTS, useLongPressDiscovered } from '../../mascot';
import { ACHIEVEMENTS } from '../progress/AchievementsSection';
import { useAchievementCount } from '../shared/useAchievementCount';
import { StreakFlame } from '../shared/StreakFlame';
import { XPGainAnimation } from '../shared/XPGainAnimation';
import { StreakMilestoneBurst, getCrossedMilestone } from '../shared/StreakMilestoneBurst';
import { LevelUpShockwave, LEVEL_UP_FILL_MS, LEVEL_UP_BURST_MS } from '../shared/LevelUpShockwave';
import { useStreakInDangerNudge } from '../../app/hooks/useStreakInDangerNudge';
import { getTimeContext } from '../../utils/timeContext';
import { useActiveAssignments } from '../../app/hooks/useActiveAssignments';
interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  currentView: string;
  onNavigate: (view: string) => void;
  onLogout?: () => void;
}
export function MobileNav({
  isOpen,
  onClose,
  currentView,
  onNavigate,
  onLogout
}: MobileNavProps) {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const {
    accounts,
    state,
    currentLearnerDisplayName,
    activeAccount,
    viewingLearnerId
  } = useAppStore();
  // Derive values from store
  const stats = state.stats;
  const displayName = currentLearnerDisplayName;
  const activeAccountRole = viewingLearnerId ? 'learner' : activeAccount?.role || 'learner';
  const isLearner = activeAccountRole === 'learner';
  // Load custom avatar for learners
  useEffect(() => {
    if (isLearner && isOpen) {
      const loadAvatar = async () => {
        const prefs = await dataLayer.getLearnerPreferences();
        if (prefs?.avatarUrl) {
          setAvatarUrl(prefs.avatarUrl);
        }
      };
      loadAvatar();
    }
  }, [isLearner, isOpen, accounts?.activeAccountId]);
  // Long-press discovery now derived from the easter-egg registry
  // (audit S4, ronde 3 2026).
  const hasDiscoveredLongPress = useLongPressDiscovered();
  const {
    selectedVariant
  } = useDjaySelection();
  // Use shared hook for variant unlock counting (single source of truth)
  const {
    unlockedCount
  } = useVariantUnlocks(hasDiscoveredLongPress);
  // Use shared achievement count hook (single source of truth)
  const unlockedAchievementsCount = useAchievementCount();
  // Active assignments (audit P1-3) — single source of truth across
  // HomePage, Sidebar and MobileNav. Only the `hasAny` flag is used here.
  const {
    hasAny: hasActiveAssignments
  } = useActiveAssignments(isLearner ? activeAccount?.id ?? null : null);
  // Prevent body scroll when menu is open
  useBodyScrollLock(isOpen);
  const navItems = getLearnerNavItems();
  const handleNavigate = (view: string) => {
    onNavigate(view);
    onClose();
  };
  // ── Levende drawer (App-Feel §3.1 / §3.4 mobile pendant) ──
  // Mirror the desktop sidebar's "living" elements: contextual Djay emotion,
  // streak flame, XP bar with shine pulse + floating "+X XP" on gain.
  const streak = activeAccount?.appState?.streak?.currentTotalStreak || 0;
  const currentXp = stats?.xp ?? 0;
  const progressToNextLevel = stats?.xp != null ? getProgressToNextLevel(stats.xp).percent : 0;
  const prevXpRef = useRef<number>(currentXp);
  const [xpGain, setXpGain] = useState<number>(0);
  const [xpPulseKey, setXpPulseKey] = useState<number>(0);
  useEffect(() => {
    const prev = prevXpRef.current;
    if (currentXp > prev) {
      setXpGain(currentXp - prev);
      setXpPulseKey(k => k + 1);
    }
    prevXpRef.current = currentXp;
  }, [currentXp]);
  // ── Level-up shockwave (App-Feel §16.B) — mobile pendant of the desktop
  // sidebar choreography. Same fill → burst → reset state machine so the
  // mobile drawer celebrates level-ups consistently. See Sidebar.tsx for
  // the full rationale.
  const currentLevel = stats?.level ?? 1;
  const prevLevelRef = useRef<number>(currentLevel);
  const [levelUpPhase, setLevelUpPhase] = useState<'idle' | 'filling' | 'bursting'>('idle');
  const [levelUpKey, setLevelUpKey] = useState<number>(0);
  const [levelUpFor, setLevelUpFor] = useState<number>(currentLevel);
  useEffect(() => {
    const prev = prevLevelRef.current;
    if (currentLevel > prev) {
      setLevelUpFor(currentLevel);
      setLevelUpKey(k => k + 1);
      setLevelUpPhase('filling');
      const t1 = setTimeout(() => setLevelUpPhase('bursting'), LEVEL_UP_FILL_MS);
      const t2 = setTimeout(() => setLevelUpPhase('idle'), LEVEL_UP_FILL_MS + LEVEL_UP_BURST_MS);
      prevLevelRef.current = currentLevel;
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    prevLevelRef.current = currentLevel;
  }, [currentLevel]);
  const effectiveBarFill = levelUpPhase === 'idle' ? progressToNextLevel : 100;
  // Contextual Djay emotion: tijd → streak → fallback
  // Shared time-of-day window (audit §P2-6) — replaces the inline
  // `hour < 8 || hour > 20` check so MobileNav stays in sync with the
  // Sidebar and FloatingDjay sleepy-window.
  let drawerEmotion: DjayEmotion = 'happy';
  if (isLearner) {
    const {
      djaySleepy
    } = getTimeContext();
    if (djaySleepy) drawerEmotion = 'sleeping';else if (streak >= 5) drawerEmotion = 'excited';else if (streak === 0) drawerEmotion = 'encouraging';
  }
  // ── Streak milestone burst (mirror of desktop sidebar) ──
  const prevStreakRef = useRef<number>(streak);
  const [streakBurst, setStreakBurst] = useState<{
    milestone: number;
    key: number;
  } | null>(null);
  useEffect(() => {
    const prev = prevStreakRef.current;
    const crossed = getCrossedMilestone(prev, streak);
    if (crossed !== null) {
      setStreakBurst(b => ({
        milestone: crossed,
        key: (b?.key ?? 0) + 1
      }));
    }
    prevStreakRef.current = streak;
  }, [streak]);
  // ── Streak-in-danger nudge (App-Feel §16.C) ──
  const {
    inDanger: streakInDanger
  } = useStreakInDangerNudge();
  return <AnimatePresence data-id="element-1416">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 md:hidden" data-id="element-1417" />

          {/* Menu Panel */}
          <motion.div className="fixed inset-y-0 left-0 w-80 bg-white shadow-2xl z-50 flex flex-col" initial={{
        x: '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '-100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} data-id="element-1418">
            {/* Header — DjayAvatar replaces the "M" logo for App-Feel P2 ("Djay is Overal") */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200" data-id="element-1419">
              <div className="flex items-center gap-3" data-id="element-1420">
                <motion.div className="flex-shrink-0" whileTap={{
              scale: 0.92,
              rotate: -6
            }} data-id="element-1421">
                  {isLearner ? <DjayAvatar size="md" emotion={drawerEmotion} variant={selectedVariant} data-id="element-1422" /> : <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center shadow-md transform rotate-3" data-id="element-1423">
                      <span className="text-white font-black text-xl" data-id="element-1424">M</span>
                    </div>}
                </motion.div>
                <span className="font-black text-slate-800 text-xl tracking-tight" data-id="element-1425">
                  {APP_NAME}
                </span>
              </div>
              <button onClick={onClose} className="p-2 bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800 rounded-full transition-colors" aria-label="Sluiten" data-id="element-1426">
                <X className="w-6 h-6 stroke-[2.5]" data-id="element-1427" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto py-4 px-4" data-id="element-1428">
              <div className="space-y-5" data-id="element-1429">
                {/* Profile Card — Levende drawer: streak flame + animated XP bar */}
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-100" data-id="element-1430">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1431">
                    {isLearner && avatarUrl ? <img src={avatarUrl} alt={displayName} className="w-12 h-12 rounded-full object-cover flex-shrink-0" data-id="element-1432" /> : <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-1433">
                        {displayName.charAt(0).toUpperCase()}
                      </div>}
                    <div className="flex-1 min-w-0" data-id="element-1434">
                      <p className="font-semibold text-gray-900 truncate" data-id="element-1435">
                        {displayName}
                      </p>
                      {stats && <p className="text-xs text-gray-500" data-id="element-1436">
                          Level {stats.level}
                        </p>}
                    </div>
                    {isLearner && <div className="flex-shrink-0 relative" data-id="element-1437">
                        <StreakFlame streak={streak} size="sm" inDanger={streakInDanger} data-id="element-1438" />
                        {streakBurst && <StreakMilestoneBurst key={streakBurst.key} milestone={streakBurst.milestone} onComplete={() => setStreakBurst(null)} data-id="element-1439" />}
                      </div>}
                  </div>
                  {isLearner && stats && <div className="space-y-1 relative" data-id="element-1440">
                      <div className="flex items-center justify-between text-xs text-gray-600" data-id="element-1441">
                        <span data-id="element-1442">XP</span>
                        <span data-id="element-1443">
                          {stats.xp} /{' '}
                          {getProgressToNextLevel(stats.xp).nextLevelXP}
                        </span>
                      </div>
                      <div className="h-2 bg-white/70 rounded-full overflow-hidden relative" data-id="element-1444">
                        <motion.div className="h-full bg-gradient-to-r from-purple-500 to-blue-500" initial={{
                    width: 0
                  }} animate={{
                    width: `${effectiveBarFill}%`
                  }} transition={{
                    duration: levelUpPhase === 'filling' ? 0.45 : 0.5,
                    ease: 'easeOut'
                  }} data-id="element-1445" />
                        <AnimatePresence data-id="element-1446">
                          {xpPulseKey > 0 && <motion.div key={xpPulseKey} className="absolute inset-y-0 w-8 pointer-events-none" style={{
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)',
                      mixBlendMode: 'screen'
                    }} initial={{
                      x: '-100%',
                      opacity: 0
                    }} animate={{
                      x: '420%',
                      opacity: [0, 1, 0]
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.9,
                      ease: 'easeOut'
                    }} data-id="element-1447" />}
                        </AnimatePresence>
                      </div>
                      {xpGain > 0 && <XPGainAnimation key={xpPulseKey} amount={xpGain} className="left-1/2 -translate-x-1/2 -top-2" onComplete={() => setXpGain(0)} data-id="element-1448" />}
                      {/* Level-up shockwave (§16.B) — fires once per level
                       boundary. Mirror of the desktop sidebar overlay. */}
                      {levelUpPhase !== 'idle' && <LevelUpShockwave key={levelUpKey} level={levelUpFor} data-id="element-1449" />}
                    </div>}
                </div>

                {/* Mijn Collectie Card */}
                {isLearner && <motion.button onClick={() => handleNavigate('collection')} className={`
                      w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left
                      ${currentView === 'collection' ? 'border-orange-400 bg-orange-50 ring-2 ring-orange-400 ring-offset-1' : 'border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 hover:border-orange-300'}
                    `} whileTap={{
              scale: 0.98
            }} data-id="element-1450">
                    <div className="relative flex-shrink-0" data-id="element-1451">
                      <motion.div animate={{
                  y: [0, -3, 0]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-1452">
                        <DjayAvatar size="sm" emotion="excited" variant={selectedVariant} data-id="element-1453" />
                      </motion.div>
                    </div>
                    <div className="flex-1 overflow-hidden" data-id="element-1454">
                      <span className="block font-black text-orange-900 text-base truncate" data-id="element-1455">
                        Mijn Collectie
                      </span>
                      <span className="block text-xs font-bold text-orange-600" data-id="element-1456">
                        {unlockedCount}/{TOTAL_VARIANTS} Varianten •{' '}
                        {unlockedAchievementsCount}/{ACHIEVEMENTS.length}{' '}
                        Achievements
                      </span>
                    </div>
                  </motion.button>}

                {/* Navigation Items */}
                <nav className="space-y-1.5" data-id="element-1457">
                  {navItems.map(item => {
                const isActive = currentView === item.id;
                const Icon = item.icon;
                return <motion.button key={item.id} onClick={() => handleNavigate(item.id)} className={`
                          w-full flex items-center gap-3 px-4 py-3 rounded-lg
                          transition-colors
                          ${isActive ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-purple-600' : 'text-gray-700 hover:bg-gray-100'}
                        `} whileTap={{
                  scale: 0.98
                }} data-id="element-1458">
                        <div className="relative flex-shrink-0" data-id="element-1459">
                          <Icon className="w-6 h-6" data-id="element-1460" />
                          {item.id === 'assessment' && hasActiveAssignments && <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 border-2 border-white rounded-full" data-id="element-1461" />}
                        </div>
                        <span className="font-medium text-base" data-id="element-1462">
                          {item.label}
                        </span>
                      </motion.button>;
              })}
                </nav>
              </div>
            </div>

            {/* Footer */}
            {onLogout && <div className="border-t border-gray-200 p-4" data-id="element-1463">
                <button onClick={() => {
            onClose();
            onLogout();
          }} className="flex items-center justify-center gap-3 text-slate-500 hover:text-rose-600 font-bold w-full p-4 rounded-2xl hover:bg-rose-50 border-2 border-transparent hover:border-rose-200 transition-colors" data-id="element-1464">
                  <LogOut className="w-6 h-6 stroke-[2.5]" data-id="element-1465" />
                  <span className="text-lg" data-id="element-1466">Uitloggen</span>
                </button>
              </div>}
          </motion.div>
        </>}
    </AnimatePresence>;
}